import { PushNotifications, Token, PushNotificationSchema, ActionPerformed } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';
import { Api } from '@/services/ApiService';
import { SchultenMedia } from '@/services/DataService';
import { navigateToNotification } from '@/utils/notificationRouter';
import webPushService from '@/services/WebPushService';

class PushNotificationService {
  private initialized = false;

  async initialize() {
    if (this.initialized) return;

    if (Capacitor.isNativePlatform()) {
      await this.initializeNative();
    } else {
      await webPushService.initialize();
    }

    this.initialized = true;
  }

  private async initializeNative() {
    const permStatus = await PushNotifications.checkPermissions();

    if (permStatus.receive === 'prompt') {
      const permission = await PushNotifications.requestPermissions();
      if (permission.receive !== 'granted') {
        console.log('Push notification permission denied');
        return;
      }
    } else if (permStatus.receive !== 'granted') {
      console.log('Push notification permission not granted');
      return;
    }

    this.setupNativeListeners();
    await PushNotifications.register();
  }

  private setupNativeListeners() {
    PushNotifications.addListener('registration', async (token: Token) => {
      console.log('Push registration token:', token.value);
      await this.registerDeviceToken(token.value);
    });

    PushNotifications.addListener('registrationError', (error) => {
      console.error('Push registration error:', error);
    });

    PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {
      console.log('Push notification received in foreground:', notification);
      // Foreground notifications are handled by Pusher, so we can ignore or show toast
    });

    PushNotifications.addListener('pushNotificationActionPerformed', (action: ActionPerformed) => {
      console.log('Push notification tapped:', action);
      this.handleNotificationTap(action.notification);
    });
  }

  private async registerDeviceToken(token: string) {
    try {
      const response = await Api.post(SchultenMedia.API.notification.saveToken, {
        device_token: token,
      });

      if (response?.error) {
        console.error('Failed to register device token:', response.message);
      } else {
        console.log('Device token registered successfully');
      }
    } catch (error) {
      console.error('Error registering device token:', error);
    }
  }

  private handleNotificationTap(notification: PushNotificationSchema) {
    const data = notification.data;
    navigateToNotification(data?.type, data);
  }

  cleanup() {
    if (Capacitor.isNativePlatform()) {
      PushNotifications.removeAllListeners();
    } else {
      webPushService.cleanup();
    }
    this.initialized = false;
  }
}

export default new PushNotificationService();
